import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

// @ts-expect-error - authuser.js は JS で型定義なし
import { useGetCurrentUserQuery } from "../accounts/authuser";
import {
  getAuthTokenFromCookies,
  removeAuthTokenFromCookies,
} from "../../common/utils";
import { createSpaSocket, type Envelope, type SpaSocket } from "../../utils/spaSocket";
import {
  resetPresence,
  setPresenceConnected,
  setPresenceSnapshot,
  userOffline,
  userOnline,
} from "./presenceSlice";

type PresenceSnapshotPayload = { online_user_ids: number[] };
type PresenceUserPayload = { user_id: number };

interface PresenceSocketProviderProps {
  children: React.ReactNode;
}

/**
 * ログイン中ユーザー向けに `/ws/spa/presence` を接続・維持する Provider。
 *
 * - token / user.id 確定後にのみ接続開始。ログアウトで切断。
 * - 認証失敗 (4401) 時は cookie を削除してログイン画面へ遷移。
 * - `/ws/spa/events`（メッセンジャー系イベント）は `MessengerSocketProvider` が担当する。
 */
const PresenceSocketProvider: React.FC<PresenceSocketProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const { data: currentUserData } = useGetCurrentUserQuery();
  const userId: number | undefined = currentUserData?.data?.id;
  const presenceSocketRef = useRef<SpaSocket | null>(null);

  useEffect(() => {
    const token = getAuthTokenFromCookies();
    if (!token || !userId) {
      return;
    }

    const handleUnauthorized = () => {
      removeAuthTokenFromCookies();
      dispatch(resetPresence());
      window.location.replace("/signin");
    };

    const handlePresenceMessage = (ev: Envelope) => {
      switch (ev.type) {
        case "presence.snapshot": {
          const payload = ev.payload as PresenceSnapshotPayload;
          const ids = Array.isArray(payload?.online_user_ids) ? payload.online_user_ids : [];
          dispatch(setPresenceSnapshot(ids));
          break;
        }
        case "presence.user_online": {
          const payload = ev.payload as PresenceUserPayload;
          if (typeof payload?.user_id === "number") {
            dispatch(userOnline(payload.user_id));
          }
          break;
        }
        case "presence.user_offline": {
          const payload = ev.payload as PresenceUserPayload;
          if (typeof payload?.user_id === "number") {
            dispatch(userOffline(payload.user_id));
          }
          break;
        }
        default:
          // 未知の type はクライアント側で無視（前方互換）。
          break;
      }
    };

    presenceSocketRef.current = createSpaSocket({
      path: "presence",
      token,
      onOpen: () => dispatch(setPresenceConnected(true)),
      onMessage: handlePresenceMessage,
      onUnauthorized: handleUnauthorized,
      reconnectOnVisible: true,
    });

    return () => {
      presenceSocketRef.current?.disconnect();
      presenceSocketRef.current = null;
      dispatch(setPresenceConnected(false));
      dispatch(resetPresence());
    };
  }, [dispatch, userId]);

  return <>{children}</>;
};

export default PresenceSocketProvider;
